import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";
import { getCanonicalUrl } from "@/config/site";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const MAGENTA_BEAM = "#FF4DB8";
const SPARK_BLUE = "#4E9CFF";

interface LinkCardProps {
  genieIcon: GenieIconName;
  title: string;
  description: string;
  to: string;
}

const LinkCard = ({ genieIcon, title, description, to }: LinkCardProps) => (
  <Link
    to={to}
    className="flex flex-col items-center p-6 rounded-2xl transition-all duration-300 hover:scale-[1.03] min-w-[160px] max-w-[200px] flex-1 bg-white shadow-md hover:shadow-lg"
    style={{ border: "2px solid transparent" }}
  >
    <div
      className="mb-3 p-3 rounded-xl"
      style={{
        background: `linear-gradient(135deg, ${MAGENTA_BEAM}15, ${SPARK_BLUE}15)`,
      }}
    >
      <GenieIcon
        name={genieIcon}
        size={36}
        color={MAGENTA_BEAM}
        secondaryColor={SPARK_BLUE}
        gradientEnabled={true}
        gradientAngle={135}
        label={title}
      />
    </div>
    <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
    <p className="text-sm text-gray-500 text-center">{description}</p>
  </Link>
);

interface GenieNotFoundProps {
  isInactive?: boolean;
}

const GenieNotFound = ({ isInactive = false }: GenieNotFoundProps) => {
  const links = [
    {
      genieIcon: "dashboard" as const,
      title: "Home",
      description: "Back to the homepage",
      to: "/",
    },
    {
      genieIcon: "chat" as const,
      title: "Talk to Genie",
      description: "Chat with our onboarding genie",
      to: "/talk-to-genie",
    },
    {
      genieIcon: "code" as const,
      title: "How It Works",
      description: "Learn about Help Genie",
      to: "/how-it-works",
    },
    {
      genieIcon: "chat-check" as const,
      title: "Contact Us",
      description: "Get in touch",
      to: "/contact",
    },
  ];

  const title = isInactive ? "Genie Not Available" : "Genie Not Found";
  const message = isInactive
    ? "This genie is currently inactive and cannot be accessed."
    : "This genie is not available or you don't have access to it.";
  const heading = isInactive
    ? "This genie is taking a break..."
    : "This genie vanished into thin air...";

  return (
    <>
      <Helmet>
        <title>{title} | Help Genie</title>
        <meta name="description" content={message} />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={getCanonicalUrl("/404")} />
      </Helmet>

      <div
        className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
        style={{
          background: `linear-gradient(135deg, ${MAGENTA_BEAM}08, white 50%, ${SPARK_BLUE}08)`,
        }}
      >
        {/* Logo */}
        <div className="flex justify-center mb-6 p-2">
          <HelpGenieLogo size={56} />
        </div>

        {/* Image placeholder */}
        <div className="mb-8 max-w-md w-full">
          <img
            src="/genie.svg"
            alt={title}
            className="w-full h-auto rounded-2xl"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        </div>

        {/* Header */}
        <div className="text-center mb-10">
          <h1
            className="text-4xl md:text-5xl font-bold mb-3 leading-tight pb-2"
            style={{
              background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {heading}
          </h1>
          <p className="text-gray-500 text-lg max-w-md mx-auto">{message}</p>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8 w-full max-w-md">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-gray-400 text-sm">What's next?</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Links Grid */}
        <div className="flex flex-wrap justify-center gap-4 max-w-4xl">
          {links.map((link) => (
            <LinkCard
              key={link.to}
              genieIcon={link.genieIcon}
              title={link.title}
              description={link.description}
              to={link.to}
            />
          ))}
        </div>
      </div>
    </>
  );
};

export default GenieNotFound;
